/**
 * @fileoverview Indicador visual de status de salvamento automático
 * @module components/dre/SaveStatusIndicator
 * 
 * Componente que mostra:
 * - "Salvando..." com spinner quando está salvando
 * - "Salvo" com checkmark quando foi salvo recentemente
 * - "Alterações não salvas" com warning quando há mudanças pendentes
 * - Timestamp da última vez que foi salvo
 */

import { Box, Chip, CircularProgress, Tooltip } from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  CloudDone as CloudDoneIcon
} from '@mui/icons-material';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';

interface SaveStatusIndicatorProps {
  isSaving: boolean;
  lastSaved: Date | null;
  hasUnsavedChanges: boolean;
}

/**
 * Indicador visual do status de salvamento automático
 */
export function SaveStatusIndicator({
  isSaving,
  lastSaved,
  hasUnsavedChanges
}: SaveStatusIndicatorProps) {
  // Estado: Salvando
  if (isSaving) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <CircularProgress size={16} />
        <Chip
          label="Salvando..."
          size="small"
          color="info"
          variant="outlined"
        />
      </Box>
    );
  }

  // Estado: Alterações não salvas
  if (hasUnsavedChanges) {
    return (
      <Tooltip title="As alterações serão salvas automaticamente em breve">
        <Chip
          icon={<WarningIcon />}
          label="Alterações não salvas"
          size="small"
          color="warning"
          variant="outlined"
        />
      </Tooltip>
    );
  }

  // Estado: Salvo (mostra timestamp se disponível)
  if (lastSaved) {
    const timeAgo = formatDistanceToNow(lastSaved, {
      addSuffix: true,
      locale: ptBR
    });

    return (
      <Tooltip title={`Última versão salva ${timeAgo}`}>
        <Chip
          icon={<CheckCircleIcon />}
          label={`Salvo ${timeAgo}`}
          size="small"
          color="success"
          variant="outlined"
          sx={{
            '& .MuiChip-icon': {
              color: 'success.main'
            }
          }}
        />
      </Tooltip>
    );
  }

  // Estado: Nenhum salvamento ainda
  return (
    <Chip
      icon={<CloudDoneIcon />}
      label="Auto-save ativo"
      size="small"
      color="default"
      variant="outlined"
    />
  );
}
